// VER-218: reset-password page (target of the reset email link).
// /console/reset-password/:token -> validate -> set new password -> back to login.
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { App, Button, Card, Form, Input, Typography, Spin, Result } from "antd";
import { authApi } from "../console/consoleApi";

const { Title, Text } = Typography;

const errorMessage = (err) =>
  err?.response?.data?.error?.message || "Something went wrong. Please try again.";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [state, setState] = useState("validating"); // validating | valid | invalid
  const [email, setEmail] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.validateReset(token);
        if (!cancelled) {
          setEmail(res.data.data.email);
          setState("valid");
        }
      } catch {
        if (!cancelled) setState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async ({ password }) => {
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      message.success("Password updated. Please sign in.");
      navigate("/console/login", { replace: true });
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <Card style={{ width: 420 }}>
        {state === "validating" && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Checking your reset link…</Text>
            </div>
          </div>
        )}

        {state === "invalid" && (
          <Result
            status="warning"
            title="Reset link not valid"
            subTitle="This link is invalid or has expired. Request a new one from the sign-in page."
            extra={
              <Link to="/console/forgot-password">
                <Button>Request a new link</Button>
              </Link>
            }
          />
        )}

        {state === "valid" && (
          <>
            <Title level={3} style={{ marginBottom: 4 }}>
              Set a new password
            </Title>
            <Text type="secondary">
              For <b>{email}</b>
            </Text>
            <Form
              layout="vertical"
              onFinish={handleSubmit}
              style={{ marginTop: 16 }}
              disabled={submitting}
              requiredMark={false}
            >
              <Form.Item
                name="password"
                label="New password"
                hasFeedback
                rules={[
                  { required: true, message: "Enter a password" },
                  { min: 12, message: "At least 12 characters" },
                  { max: 64, message: "At most 64 characters" },
                ]}
              >
                <Input.Password
                  placeholder="12–64 characters"
                  size="large"
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item
                name="confirm"
                label="Confirm password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  { required: true, message: "Confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) return Promise.resolve();
                      return Promise.reject(new Error("Passwords don't match"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Re-enter password"
                  size="large"
                  autoComplete="new-password"
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
                Update password
              </Button>
            </Form>
          </>
        )}
      </Card>
    </div>
  );
}
